import React, {memo, useRef, useCallback, useEffect} from 'react';
import {hasWeatherForLocationTypes} from '@/common/constants/locationType';
import {RouteName} from '@/app/constants/routing';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {useLocalizer} from '@/common/services/localizer';
import {useSearchSuggest} from '@/app/hooks/useSearchSuggest';
import {useSelectKeyboardControl} from '@/common/helpers/hooks/useSelectKeyboardControl';
import {SearchSuggests} from '../SearchSuggests';
import styles from './SearchField.css';

const debounceInterval = 200;
const suggestCountLimit = 50;

export const SearchField = memo(() => {
	const t = useLocalizer();

	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();

	const fieldRef = useRef<HTMLInputElement>(null);

	const {
		searchString,
		suggestList,
		suggestShowAllowed,
		handleFieldFocusin,
		handleFieldFocusout,
		handleSearchStringChange,
		handleSearchStringClean,
	} = useSearchSuggest({suggestCountLimit, debounceInterval});

	const {focusedIndex, handleKeyboardControlEvent, handleFocusIndexReset, setFocusedIndex} = useSelectKeyboardControl(
		{listLength: suggestList.length},
	);

	// Navigate to focused (or first) location on form submit
	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const suggest = focusedIndex === -1 ? suggestList[0] : suggestList[focusedIndex];
		if (!suggest || (!searchString && !suggestShowAllowed)) {
			return;
		}

		const hasWeather = !suggest.type || hasWeatherForLocationTypes.includes(suggest.type);
		const url = hasWeather
			? urlBuilder(RouteName.weather, {location: suggest})
			: urlBuilder(RouteName.map, {location: suggest.id});
		history!.push(url);

		handleSearchStringClean();
		handleFocusIndexReset();
		fieldRef.current!.blur();
	};

	const handleBeforeNavigate = useCallback(() => {
		handleSearchStringClean();
		handleFocusIndexReset();
		fieldRef.current!.blur();
	}, []);

	const handleInputReset = useCallback(() => {
		handleSearchStringClean();
		handleFocusIndexReset();
		Promise.resolve().then(() => {
			fieldRef.current!.focus();
		});
	}, []);

	// Reset focused item on the list change
	useEffect(() => {
		if (focusedIndex !== -1) {
			handleFocusIndexReset();
		}
	}, [searchString]);

	// Reset focused item on the suggest hide after focusout
	useEffect(() => {
		if (!suggestShowAllowed && focusedIndex !== -1) {
			handleFocusIndexReset();
		}
	}, [suggestShowAllowed]);

	useEffect(() => {
		// Workaround to React bug with old Firefox version (<= 54) - no onFocus event triggers
		const ua = navigator.userAgent;
		if (ua.includes('Firefox/') && !ua.includes('Seamonkey/')) {
			const ffVersion = /Firefox\/(\w+)/.exec(ua)?.[1];
			if (!(Number(ffVersion) <= 54)) {
				return;
			}
		} else {
			return;
		}

		fieldRef.current!.addEventListener('focus', handleFieldFocusin);
		fieldRef.current!.addEventListener('blur', handleFieldFocusout);
	}, []);

	return (
		<div className={styles.root}>
			<form className={styles.form} onSubmit={handleSubmit}>
				<input
					ref={fieldRef}
					className={styles.field}
					value={searchString}
					type='search'
					autoComplete='off'
					maxLength={100}
					placeholder={t('searchHint')}
					aria-label={t('searchFieldLabel')}
					onFocus={handleFieldFocusin}
					onBlur={handleFieldFocusout}
					onChange={handleSearchStringChange}
					onKeyDown={suggestList.length === 0 ? undefined : handleKeyboardControlEvent}
				/>
				{searchString && (
					<button
						className={styles.clear}
						type='button'
						aria-label={t('clearButtonLabel')}
						onClick={handleInputReset}
					/>
				)}
				<button
					className={styles.submit}
					type='submit'
					disabled={suggestList.length === 0 || !suggestShowAllowed}
					aria-label={t('searchButtonLabel')}
				/>
			</form>

			{suggestShowAllowed && (suggestList.length !== 0 || !!searchString) && (
				<SearchSuggests
					suggests={suggestList}
					focusedIndex={focusedIndex}
					onFocusRequire={setFocusedIndex}
					onBeforeNavigate={handleBeforeNavigate}
				/>
			)}
		</div>
	);
});

SearchField.displayName = 'SearchField';
